import { FAQ, type FaqKey } from '../../content/faq';
import { SITE_CONFIG } from '../../config/site-config';
import { $ } from '../../lib/dom';
import { stripTags } from '../../lib/html';
import type { ResolvedPage } from './page-registry';

const ROUTE_LD_ID = 'route-ld';

type StructuredData = Record<string, unknown>;

function faqStructuredData(keys: readonly FaqKey[]): StructuredData {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: keys.map((key) => {
      const [question, answer] = FAQ[key];
      return { '@type': 'Question', name: question, acceptedAnswer: { '@type': 'Answer', text: stripTags(answer) } };
    }),
  };
}

function routeStructuredData(page: ResolvedPage, url: string): StructuredData | null {
  if (page.faq) return faqStructuredData(page.faq);
  if (page.article) {
    return {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: page.article.title,
      datePublished: page.article.date,
      author: { '@type': 'Organization', name: page.article.author ?? 'ReTax360' },
      mainEntityOfPage: url,
    };
  }
  return null;
}

/** Updates the document title, meta tags, canonical URL and route-specific JSON-LD. */
export function applyPageMeta(page: ResolvedPage, pathWithQuery: string): void {
  const { title, description } = page.meta;
  const url = SITE_CONFIG.SITE_URL + pathWithQuery;
  const setAttribute = (selector: string, attribute: string, value: string): void => {
    $(selector)?.setAttribute(attribute, value);
  };
  document.title = title;
  setAttribute('meta[name="description"]', 'content', description);
  setAttribute('meta[property="og:title"]', 'content', title);
  setAttribute('meta[property="og:description"]', 'content', description);
  setAttribute('meta[property="og:url"]', 'content', url);
  setAttribute('meta[name="twitter:title"]', 'content', title);
  setAttribute('meta[name="twitter:description"]', 'content', description);
  setAttribute('link[rel="canonical"]', 'href', url);

  document.getElementById(ROUTE_LD_ID)?.remove();
  const data = routeStructuredData(page, url);
  if (data) {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = ROUTE_LD_ID;
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
  }
}
